import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useEffect } from 'react';

import { Analytics } from '@trello/atlassian-analytics';
import { isMemberLoggedIn } from '@trello/authentication';
import { intl } from '@trello/i18n';

import { Breadcrumbs } from 'app/src/components/Breadcrumbs';
import { getMarketingScreenInfo } from 'app/src/getMarketingScreenInfo';
import { BlankBoardTile } from './BlankBoardTile';
import type { TemplateCategoryImagePath } from './getTemplateCategoryImage';
import { getTemplateCategoryImage } from './getTemplateCategoryImage';
import { SearchWrapper } from './SearchWrapper';
import { TemplatesTile } from './TemplatesTile';
import type { Template } from './types';
import { WhatIsTrelloBanner } from './WhatIsTrelloBanner';

import styles from './TemplateCategory.less';

export const TemplateCategory: FunctionComponent<{
  category: string;
  templates: Template[];
  isInAppGallery: boolean;
}> = ({ category, templates, isInAppGallery }) => {
  useEffect(() => {
    const { url, referrerUrl, referrerScreenName } = getMarketingScreenInfo();
    Analytics.sendMarketingScreenEvent({
      url,
      referrerUrl,
      referrerScreenName,
      event: {
        source: 'templateCategoryScreen',
        name: 'templateCategoryScreenV2',
        attributes: {
          category,
          totalListed: templates.length,
        },
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tiles = templates
    .sort((a, b) => b.templateGallery.precedence - a.templateGallery.precedence)
    .map((t) => {
      if (t.templateGallery.category === category) {
        return (
          <TemplatesTile
            key={t.id}
            avatarUrl={t.templateGallery.avatarUrl}
            avatarShape={t.templateGallery.avatarShape}
            backgroundColor={t.prefs.backgroundColor}
            backgroundUrl={t.prefs.backgroundImage}
            blurb={t.templateGallery.blurb}
            boardId={t.id}
            byline={t.templateGallery.byline}
            category={t.templateGallery.category}
            idOrganization={t.idOrganization}
            shortLink={t.shortLink}
            title={t.name}
            viewCount={t.templateGallery.stats.viewCount}
            copyCount={t.templateGallery.stats.copyCount}
          />
        );
      }
    })
    .filter((tile) => tile);

  const headerIcon = getTemplateCategoryImage(
    category as TemplateCategoryImagePath,
  );

  return (
    <>
      <div className={styles.templateBreadCrumb}>
        <Breadcrumbs
          items={[
            {
              title: intl.formatMessage({
                id: 'templates.templates.template-gallery',
                defaultMessage: 'Template gallery',
                description: 'A link to the template gallery',
              }),
              href: '/templates',
            },
            {
              title: intl
                .formatMessage({
                  id: `templates.templates.${category}`,
                  defaultMessage: 'Category',
                  description:
                    'A link to a gallery of templates for a particular category',
                })
                .replace(/&amp;/g, '&'),
              href: `/templates/${category}`,
            },
          ]}
        />
        {isInAppGallery && <SearchWrapper templates={templates} />}
      </div>
      <h1 className={styles.templateGalleryHeaderContainer}>
        <img
          className={styles.templateGalleryIcon}
          src={headerIcon}
          alt={`${intl
            .formatMessage({
              id: `templates.templates.${category}`,
              defaultMessage: 'Category',
              description: 'Category',
            })
            .replace(/&amp;/g, '&')} Icon`}
        />
        <span className={styles.templateGalleryHeader}>
          {intl
            .formatMessage({
              id: `templates.templates.${category}-templates`,
              defaultMessage: 'Category Templates',
              description: 'Category templates',
            })
            .replace(/&amp;/g, '&')}
        </span>
      </h1>
      <div className={classNames(styles.templatesTilesContainer)}>
        {tiles}
        {isMemberLoggedIn() && <BlankBoardTile />}
      </div>
      {!isInAppGallery && <WhatIsTrelloBanner isCategoryPage />}
      <div className={styles.categoryDescriptionContainer}>
        <p className={styles.categoryDescription}>
          {intl.formatMessage({
            id: `templates.templates_category_description.${category}`,
            defaultMessage: 'Category',
            description: 'A description of the current category',
          })}
        </p>
        <img
          className={styles.categoryIcon}
          src={getTemplateCategoryImage(
            category as TemplateCategoryImagePath,
            'transparent',
          )}
          alt={`${intl
            .formatMessage({
              id: `templates.templates.${category}`,
              defaultMessage: 'Category',
              description: 'Category',
            })
            .replace(/&amp;/g, '&')} Icon`}
        />
      </div>
    </>
  );
};
